import React, {useState, useEffect} from "react";
import Button from "../../components/common/Button";
import {Check} from "lucide-react";
import {AuthClient} from "../../services/apiClient";
import {API_BASE_URL} from "../../config/config";
import axiosInstance from "../../services/axiosInstance"; // Adjust the import path

interface TwoFactorAuthProps {
    qrCodeUrl: string;
    onVerifyCode: (code: string) => Promise<boolean>; // Function to verify the code
    onFinish: () => void; // Callback when setup is complete
}

const TwoFactorAuthSetup: React.FC<TwoFactorAuthProps> = ({
                                                              qrCodeUrl,
                                                              onVerifyCode,
                                                              onFinish,
                                                          }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [verificationCode, setVerificationCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleNext = async () => {
        if (currentStep === 2) {
            // Verify the code
            setLoading(true);
            const isValid = await onVerifyCode(verificationCode);
            setLoading(false);

            if (!isValid) {
                setError("Invalid code. Please try again.");
                return;
            }

            setError(null); // Clear error if verification succeeds
        }
        setCurrentStep((prev) => prev + 1);
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const renderStepContent = () => {
        if (loading) {
            return (
                <div className="text-center">
                    <p className="text-gray-700 dark:text-gray-300">
                        Loading, please wait...
                    </p>
                </div>
            );
        }

        switch (currentStep) {
            case 1:
                return (
                    <div className="text-left">
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Scan the QR code below with your authentication app (e.g., Google Authenticator or Authy).
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                            If you already have the code set up in your authenticator app, you can skip this step and proceed to verification by clicking "Next."
                        </p>
                        {qrCodeUrl ? (
                            <img
                                src={qrCodeUrl}
                                alt="QR Code"
                                className="mx-auto rounded shadow-md"
                            />
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400">
                                Unable to load QR code. Please refresh the page.
                            </p>
                        )}
                    </div>
                );
            case 2:
                return (
                    <div className="text-left">
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Enter the 6-digit code generated by your authentication app to verify.
                        </p>
                        <input
                            type="text"
                            className="w-full px-4 py-2 border rounded text-gray-700 dark:text-gray-300 dark:bg-slate-800 focus:border-blue-500 focus:outline-none"
                            placeholder="Enter code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                        />
                        {error && (
                            <p className="text-sm text-red-600 dark:text-red-500 mt-2">{error}</p>
                        )}
                    </div>
                );
            case 3:
                return (
                    <div className="text-left">
                        <p className="text-gray-700 dark:text-gray-300">
                            Two-Factor Authentication has been successfully enabled.
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 mt-2">
                            You can now use 2FA for added account security.
                        </p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div data-hs-stepper="">
            {/* Stepper Navigation */}
            <ul className="relative flex flex-row gap-x-2 mb-6">
                {[1, 2, 3].map((step) => (
                    <li
                        key={step}
                        className={`flex items-center gap-x-2 shrink basis-0 flex-1 group ${
                            currentStep >= step ? "hs-stepper-completed" : ""
                        }`}
                        data-hs-stepper-nav-item={`{ "index": ${step} }`}
                    >
                        <span className="min-w-7 min-h-7 group inline-flex items-center text-xs align-middle">
                            <span
                                className={`size-7 flex justify-center items-center shrink-0 rounded-full ${
                                    currentStep >= step
                                        ? "bg-teal-500 text-white"
                                        : "bg-gray-100 text-gray-800"
                                }`}
                            >
                                {currentStep > step ? (
                                    <Check className="h-5 w-5"/>
                                ) : (
                                    step
                                )}
                            </span>
                            <span className="ms-2 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                Step {step}
                            </span>
                        </span>
                        {step < 3 && (
                            <div
                                className={`w-full h-px flex-1 ${
                                    currentStep > step
                                        ? "bg-teal-500"
                                        : "bg-gray-200"
                                }`}
                            ></div>
                        )}
                    </li>
                ))}
            </ul>

            {/* Stepper Content */}
            <div className="mt-5 sm:mt-8">{renderStepContent()}</div>

            {/* Button Group */}
            <div className="mt-5 flex justify-end items-center gap-x-2">
                {currentStep > 1 && (
                    <Button onClick={handleBack} variant="secondary" disabled={loading}>
                        Back
                    </Button>
                )}
                {currentStep < 3 && (
                    <Button
                        onClick={handleNext}
                        variant="primary"
                        disabled={loading || (currentStep === 2 && !verificationCode)}
                    >
                        {currentStep === 2 ? "Verify" : "Next"}
                    </Button>
                )}
                {currentStep === 3 && (
                    <Button onClick={onFinish} variant="primary">
                        Finish
                    </Button>
                )}
            </div>
        </div>
    );
};

export default TwoFactorAuthSetup;
